import { Cpu, Info, Zap } from "lucide-react";
import * as React from "react";

import type { ResourceOverride } from "@/api/submission";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

/**
 * Submit-time CPU/GPU resource-class selector (flowstack#620).
 *
 * Pre-filled from the tile's DECLARED ``resources`` (the safe default)
 * and user-overridable per submission. The class toggle (CPU/GPU) is
 * the routing-critical control — the server routes ``gpu > 0`` to the
 * GPU cluster and ``gpu == 0`` to the CPU cluster off the resolved
 * shape this control produces.
 *
 * The control is capability-aware: a class the tile can't run on is
 * disabled (a CUDA-only tile can't go CPU; a CPU-only tile shouldn't
 * waste a GPU), mirroring the server-side guard. Leaving every field at
 * its declared value emits ``undefined`` — the request then carries no
 * override and takes the declared-shape default path.
 */

/** The tile's declared resource shape, parsed from the manifest. */
export interface DeclaredResources {
  cpu: number;
  gpu: number;
  /** RAM in GB, or null when the manifest's memory unit isn't gigabyte-ish. */
  ramGb: number | null;
}

export interface ResourceCapability {
  cpuCapable: boolean;
  gpuCapable: boolean;
}

interface RawResources {
  cpu?: unknown;
  gpu?: unknown;
  memory?: { val?: unknown; unit?: unknown } | unknown;
  resource_overrides_required_when?: unknown;
}

function asInt(value: unknown): number | null {
  // Manifest scalars are ints; guard against bool (JS has no int/bool
  // subtype trap, but a stray boolean gpu should not read as 1) and NaN.
  if (typeof value !== "number" || !Number.isFinite(value)) return null;
  return Math.trunc(value);
}

/** GB reading of a manifest ``memory`` Quantity when its unit is gigabyte-ish. */
function memoryGb(memory: unknown): number | null {
  if (!memory || typeof memory !== "object") return null;
  const q = memory as { val?: unknown; unit?: unknown };
  if (typeof q.val !== "number" || !Number.isFinite(q.val)) return null;
  const unit = typeof q.unit === "string" ? q.unit.toLowerCase() : "";
  // Shipped tiles declare "gigabyte"; accept the common GB/GiB spellings.
  if (unit.includes("giga") || unit === "gb" || unit === "gib") return q.val;
  return null;
}

/**
 * Parse the tile's declared resources out of the raw manifest payload.
 * Returns null when the manifest hasn't loaded or lacks a usable
 * ``resources.cpu`` — the caller then hides the control rather than
 * rendering an untrustworthy default.
 */
export function parseDeclaredResources(manifest: unknown): DeclaredResources | null {
  if (!manifest || typeof manifest !== "object") return null;
  const raw = (manifest as { resources?: unknown }).resources;
  if (!raw || typeof raw !== "object") return null;
  const res = raw as RawResources;
  const cpu = asInt(res.cpu);
  if (cpu === null || cpu <= 0) return null;
  const gpu = asInt(res.gpu) ?? 0;
  return { cpu, gpu: Math.max(0, gpu), ramGb: memoryGb(res.memory) };
}

/**
 * Derive ``(cpuCapable, gpuCapable)`` from the declared resources and
 * the tile's ``resource_overrides_required_when`` rules — the SAME
 * capability model the server enforces (flowstack#620). A tile is
 * CPU-capable if any of its gpu values (base or override-promoted) is
 * 0, and GPU-capable if any is > 0. Mirrors the server so the UI
 * disables exactly the combos the API would reject.
 */
export function resourceCapability(manifest: unknown): ResourceCapability {
  const declared = parseDeclaredResources(manifest);
  if (!declared) return { cpuCapable: true, gpuCapable: false };
  const gpuValues: number[] = [declared.gpu];
  const raw = (manifest as { resources?: RawResources }).resources;
  const rules = raw?.resource_overrides_required_when;
  if (Array.isArray(rules)) {
    for (const rule of rules) {
      if (!rule || typeof rule !== "object") continue;
      const override = (rule as { override?: unknown }).override;
      if (!override || typeof override !== "object") continue;
      const g = asInt((override as { gpu?: unknown }).gpu);
      if (g !== null) gpuValues.push(g);
    }
  }
  return {
    cpuCapable: gpuValues.some((g) => g === 0),
    gpuCapable: gpuValues.some((g) => g > 0),
  };
}

/** The control's live selection, derived from the declared shape + the
 * current override. */
interface EffectiveSelection {
  cpuCores: number;
  ramGb: number | null;
  gpus: number;
  klass: "cpu" | "gpu";
}

function effectiveSelection(
  declared: DeclaredResources,
  value: ResourceOverride | undefined
): EffectiveSelection {
  const gpus = value?.gpus ?? declared.gpu;
  return {
    cpuCores: value?.cpu_cores ?? declared.cpu,
    ramGb: value?.ram_gb ?? declared.ramGb,
    gpus,
    klass: gpus > 0 ? "gpu" : "cpu",
  };
}

/** Build the minimal override (only the fields that differ from the
 * declared shape). Returns undefined when nothing differs so the
 * request keeps the default-safe no-override path. */
function buildOverride(
  declared: DeclaredResources,
  sel: EffectiveSelection
): ResourceOverride | undefined {
  const out: ResourceOverride = {};
  if (sel.gpus !== declared.gpu) out.gpus = sel.gpus;
  if (sel.cpuCores !== declared.cpu) out.cpu_cores = sel.cpuCores;
  if (sel.ramGb !== null && sel.ramGb !== declared.ramGb) out.ram_gb = sel.ramGb;
  return Object.keys(out).length === 0 ? undefined : out;
}

export interface ResourceClassControlProps {
  declared: DeclaredResources;
  capability: ResourceCapability;
  value: ResourceOverride | undefined;
  onChange: (next: ResourceOverride | undefined) => void;
}

export function ResourceClassControl({
  declared,
  capability,
  value,
  onChange,
}: ResourceClassControlProps): JSX.Element {
  const sel = effectiveSelection(declared, value);
  const declaredClass: "cpu" | "gpu" = declared.gpu > 0 ? "gpu" : "cpu";
  const isOverridden = sel.klass !== declaredClass || value !== undefined;

  const emit = (nextSel: EffectiveSelection): void => {
    onChange(buildOverride(declared, nextSel));
  };

  const selectClass = (klass: "cpu" | "gpu"): void => {
    if (klass === sel.klass) return;
    // CPU → gpus 0; GPU → keep the declared GPU count if it had one,
    // else default to a single GPU (the informed opt-in).
    const gpus = klass === "gpu" ? (declared.gpu > 0 ? declared.gpu : 1) : 0;
    emit({ ...sel, gpus, klass });
  };

  return (
    <div className="flex flex-col gap-3" role="group" aria-label="Resource class">
      <div className="flex items-center justify-between gap-2">
        <span className="text-xs text-muted-foreground">
          Runs on the tile&apos;s declared class by default.
        </span>
        {isOverridden ? (
          <span className="rounded-sm bg-state-staging/15 px-1.5 py-0.5 text-[10px] uppercase tracking-wider text-state-staging">
            overridden
          </span>
        ) : (
          <span className="label-micro rounded-sm bg-surface-2 px-1.5 py-0.5 text-muted-foreground">
            default
          </span>
        )}
      </div>

      {/* CPU / GPU segmented toggle */}
      <div role="radiogroup" aria-label="Compute class" className="grid grid-cols-2 gap-2">
        <ClassOption
          label="CPU"
          icon={<Cpu className="h-3.5 w-3.5" aria-hidden />}
          selected={sel.klass === "cpu"}
          disabled={!capability.cpuCapable}
          disabledReason="This tile declares a GPU-only engine; it can't run on CPU."
          onSelect={() => selectClass("cpu")}
        />
        <ClassOption
          label="GPU"
          icon={<Zap className="h-3.5 w-3.5" aria-hidden />}
          selected={sel.klass === "gpu"}
          disabled={!capability.gpuCapable}
          disabledReason="This tile has no GPU lane; running it on GPU would waste GPU capacity."
          onSelect={() => selectClass("gpu")}
        />
      </div>

      {/* Numeric overrides */}
      <div className="grid grid-cols-2 gap-3">
        <NumberField
          id="resource-cpu-cores"
          label="cpu_cores"
          value={sel.cpuCores}
          min={1}
          step={1}
          onChange={(n) => emit({ ...sel, cpuCores: n ?? declared.cpu })}
        />
        <NumberField
          id="resource-ram-gb"
          label="ram_gb"
          value={sel.ramGb}
          min={1}
          step={1}
          placeholder={declared.ramGb === null ? "—" : undefined}
          onChange={(n) => emit({ ...sel, ramGb: n })}
        />
        {sel.klass === "gpu" && (
          <NumberField
            id="resource-gpus"
            label="gpus"
            value={sel.gpus}
            min={1}
            step={1}
            onChange={(n) => emit({ ...sel, gpus: n && n > 0 ? n : 1 })}
          />
        )}
      </div>

      {isOverridden && sel.klass !== declaredClass && (
        <p
          role="note"
          className="flex items-start gap-1.5 rounded-md border-l-4 border-state-staging bg-state-staging/5 p-2 text-[11px] text-muted-foreground"
        >
          <Info className="mt-0.5 h-3 w-3 shrink-0" aria-hidden />
          <span>
            Overriding the tile&apos;s declared {declaredClass.toUpperCase()} class to{" "}
            {sel.klass.toUpperCase()}. The default (tile declaration) is the safe choice; this
            override is an informed opt-in and is validated against the tile&apos;s engine at
            submit.
          </span>
        </p>
      )}
    </div>
  );
}

interface ClassOptionProps {
  label: string;
  icon: React.ReactNode;
  selected: boolean;
  disabled: boolean;
  disabledReason: string;
  onSelect: () => void;
}

function ClassOption({
  label,
  icon,
  selected,
  disabled,
  disabledReason,
  onSelect,
}: ClassOptionProps): JSX.Element {
  const button = (
    <button
      type="button"
      role="radio"
      aria-checked={selected}
      aria-label={label}
      disabled={disabled}
      onClick={onSelect}
      className={cn(
        "inline-flex items-center justify-center gap-1.5 rounded-md border px-3 py-2 text-sm font-medium transition-colors",
        selected
          ? "border-cyan bg-cyan/10 text-foreground"
          : // The unselected radio is still a control, so it takes the control
            // boundary and the card's own fill — not the hairline and the page
            // token, which together made it invisible in light (flowstack#1403).
            //
            // `enabled:` gates the hover rather than a second rule undoing it.
            // `disabled` suppresses pointer *events*, not the `:hover`
            // pseudo-class, and this button is deliberately left hoverable so
            // the tooltip below fires over it — so an override that reached
            // only `hover:text-*` left `hover:bg-muted` lighting the option up
            // as clickable in the one flow whose whole job is to say it is
            // not. `Button` gets there with `disabled:pointer-events-none`,
            // which is not open to us for that same tooltip reason.
            "border-input bg-card text-muted-foreground enabled:hover:bg-muted enabled:hover:text-foreground",
        disabled && "cursor-not-allowed opacity-40"
      )}
    >
      {icon}
      {label}
    </button>
  );
  if (!disabled) return button;
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        {/* Wrapper span so the tooltip still fires over a disabled button. */}
        <span className="inline-flex">{button}</span>
      </TooltipTrigger>
      <TooltipContent>{disabledReason}</TooltipContent>
    </Tooltip>
  );
}

interface NumberFieldProps {
  id: string;
  label: string;
  value: number | null;
  min: number;
  step: number;
  placeholder?: string;
  onChange: (value: number | null) => void;
}

function NumberField({
  id,
  label,
  value,
  min,
  step,
  placeholder,
  onChange,
}: NumberFieldProps): JSX.Element {
  return (
    <div className="flex flex-col gap-1.5">
      <Label htmlFor={id} className="font-mono text-xs">
        {label}
      </Label>
      <Input
        id={id}
        type="number"
        inputMode="numeric"
        min={min}
        step={step}
        placeholder={placeholder}
        value={value === null ? "" : String(value)}
        onChange={(e) => {
          if (e.target.value === "") {
            onChange(null);
            return;
          }
          const n = Number(e.target.value);
          if (!Number.isFinite(n)) return;
          onChange(n);
        }}
        className="font-mono tabular-nums"
      />
    </div>
  );
}
